//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import styled from "@emotion/styled";
import copy from "copy-to-clipboard";
import React, { useEffect, useState } from "react";
import { Icon } from "semantic-ui-react";

const COPIED_RESET_MS = 2000;

const StyledCopyableId = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: normal;
  line-height: normal;
  white-space: nowrap;
  cursor: default;

  .value {
    user-select: all;
  }

  button {
    display: inline-flex;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    cursor: pointer;
  }

  i.icon {
    margin: 0;
  }
`;

interface ICopyableIdArgs {
  label: string;
  value?: string;
  className?: string;
}

// CopyableId shows a resource identifier as "<label>: <value>" with a copy-to-clipboard icon.
// Customers are regularly asked by support for their organization or deployment ID.
export const CopyableId = ({ label, value, className }: ICopyableIdArgs) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!value) {
    return null;
  }

  const onCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    copy(value);
    setCopied(true);
  };

  return (
    <StyledCopyableId className={className} data-testid="copyableId" onClick={(e) => e.stopPropagation()}>
      <span>{label}:</span>
      <span className="value">{value}</span>
      <button type="button" aria-label="Copy to clipboard" title="Copy to clipboard" data-testid="copyableIdCopyButton" onClick={onCopy}>
        <Icon name={copied ? "checkmark" : "copy"} data-testid="copyableIdCopyIcon" />
      </button>
    </StyledCopyableId>
  );
};
